/**
 * Auth state helper utilities for consistent authentication handling
 * Based on Okta React best practices
 */

/**
 * Dev-only Okta bypass flag. The `import.meta.env.DEV &&` guard is load-bearing:
 * Vite statically replaces `import.meta.env.DEV` with the literal `false` during
 * `vite build`, so this whole expression collapses to `false` and is dead-code
 * eliminated from production bundles. It can never evaluate true in a prod build,
 * regardless of what VITE_DISABLE_AUTH is set to.
 * @returns {boolean} - True only in dev mode with VITE_DISABLE_AUTH=true
 */
export const isAuthDisabled = () => {
  return import.meta.env.DEV && import.meta.env.VITE_DISABLE_AUTH === 'true';
};

/**
 * Checks if authentication state is fully loaded and user is authenticated
 * @param {Object} authState - The authState from useOktaAuth hook
 * @returns {boolean} - True if user is authenticated with valid tokens
 */
export const isAuthenticated = (authState) => {
  if (isAuthDisabled()) return true;
  return authState?.isAuthenticated === true && authState?.accessToken?.accessToken;
};

/**
 * Checks if authentication state is still loading
 * @param {Object} authState - The authState from useOktaAuth hook
 * @returns {boolean} - True if auth state is still being determined
 */
export const isAuthLoading = (authState) => {
  if (isAuthDisabled()) return false;
  return authState === null || authState === undefined;
};

/**
 * Checks if user is definitely not authenticated (not loading)
 * @param {Object} authState - The authState from useOktaAuth hook
 * @returns {boolean} - True if user is confirmed not authenticated
 */
export const isNotAuthenticated = (authState) => {
  if (isAuthDisabled()) return false;
  return authState !== null && authState?.isAuthenticated === false;
};

/**
 * Gets the access token from authState safely
 * @param {Object} authState - The authState from useOktaAuth hook
 * @returns {string|null} - The access token or null if not available
 */
export const getAccessToken = (authState) => {
  return authState?.accessToken?.accessToken || null;
};

/**
 * Gets user claims from ID token safely
 * @param {Object} authState - The authState from useOktaAuth hook
 * @returns {Object|null} - User claims or null if not available
 */
export const getUserClaims = (authState) => {
  return authState?.idToken?.claims || null;
};

/**
 * Hook for handling auth state changes with proper loading states
 * Addresses the common issue of authState being undefined in useEffect
 */
import { useEffect, useState } from 'react';

export const useAuthStateHandler = (authState, onAuthenticated, onNotAuthenticated) => {
  const [hasHandledAuth, setHasHandledAuth] = useState(false);

  useEffect(() => {
    // Don't process if auth state is still loading
    if (isAuthLoading(authState)) {
      return;
    }

    // Reset handler flag when auth state changes
    if (!hasHandledAuth) {
      setHasHandledAuth(true);
    }

    if (isAuthenticated(authState)) {
      onAuthenticated?.(authState);
    } else if (isNotAuthenticated(authState)) {
      onNotAuthenticated?.(authState);
    }
  }, [authState, hasHandledAuth, onAuthenticated, onNotAuthenticated]);

  return {
    isLoading: isAuthLoading(authState),
    isAuthenticated: isAuthenticated(authState),
    isNotAuthenticated: isNotAuthenticated(authState),
  };
};
